import React, { useEffect, useRef, useCallback, useState } from 'react';
import { fieldStyling, showStyling } from '../../services/vgs/vgsStyling';
import { SnapIcon } from '../SnapIcon';
import BaseInput from '../BaseInput';
import useVgsShow from './hooks/useVgsShowInstance';
import logger from '../../util/logger';

const MAX_LOAD_TIME = 10 * 1000;

export default function VgsShowValue({
  value,
  label,
  replacePatterns,
  type,
  onRevealSuccess,
  onRevealFail,
  useFieldStyling,
  token,
}: {
  value?: string | null;
  label: string;
  type: VgsFieldType;
  replacePatterns?: [string, string, number?][];
  onRevealSuccess?: () => void;
  onRevealFail?: () => void;
  useFieldStyling?: boolean;
  token: string;
}): JSX.Element {
  const elRef = useRef<HTMLDivElement>(null);
  const [internalValue, setInternalValue] = useState<typeof value>();
  const [frame, setFrame] = useState<VGSShowFrame>();
  const [isRevealed, setIsRevealed] = useState(false);
  const vgsShowInstance = useVgsShow();

  const revealSuccess = useCallback((): void => {
    logger(`successfully revealed ${label}`);
    // try as we might, sometimes the iFrame can be rendered more than once
    // iterate over extra frames and remove them from DOM
    for (const extraFrame of (frame?.frames || []).slice(1)) {
      extraFrame.remove();
    }
    if (onRevealSuccess !== undefined) {
      onRevealSuccess();
    }
    setIsRevealed(true);
  }, [frame?.frames, label, onRevealSuccess]);

  // Many times the failure events for the frame (revealFail and requestFail) are not called
  // even when there is an error in the iframe.
  // This effect waits 5s and checks if the frame has been rendered.
  // If it has not, the onRevealFail callback is called.
  useEffect(() => {
    const revealTimer = setTimeout(() => {
      if (!isRevealed) {
        logger(
          `VgsShowValue: ${label} has not revealed frame in ${MAX_LOAD_TIME}ms`,
          true,
        );
        if (onRevealFail !== undefined) {
          onRevealFail();
        } else {
          throw new Error(`${label} has not loaded`);
        }
      }
    }, MAX_LOAD_TIME);
    return (): void => clearTimeout(revealTimer);
  }, [isRevealed, label, onRevealFail]);

  useEffect((): void | (() => void) => {
    if (
      vgsShowInstance !== undefined &&
      elRef.current !== undefined &&
      value !== undefined &&
      value !== null &&
      value !== internalValue
    ) {
      setInternalValue(value);
      if (frame?.frames && frame.frames.length > 0) {
        for (const internalFrame of frame.frames) {
          internalFrame.remove();
        }
      }
      setFrame(
        vgsShowInstance.request({
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
          name: 'value-text',
          method: 'POST',
          path: '/graphql',
          payload: {
            variables: { tokenizedValue: value },
            query: `query TokenizedValue($tokenizedValue: String!) {
            vaultVgsValue(value: $tokenizedValue)
          }`,
          },
          htmlWrapper: 'text',
          jsonPathSelector: 'data.vaultVgsValue',
          serializers: (replacePatterns || []).map(
            ([oldValue, newValue, count]) => {
              return vgsShowInstance.SERIALIZERS.replace(
                oldValue,
                newValue,
                count,
              );
            },
          ),
        }),
      );
    }
  }, [
    frame,
    internalValue,
    label,
    replacePatterns,
    revealSuccess,
    value,
    vgsShowInstance,
    token,
  ]);

  useEffect(() => {
    if (frame === undefined || frame.frames.length >= 1) {
      return;
    }

    const frameFail = (): void => {
      logger(`VgsShowValue: ${label} has not revealed frame `, true);
      if (onRevealFail !== undefined) {
        onRevealFail();
      }
    };

    frame.render(elRef.current, {
      ...(useFieldStyling ? fieldStyling : showStyling),
      justifyContent: 'left',
    });

    frame.on('revealSuccess', revealSuccess);
    frame.on('revealFail', () => {
      logger(
        `VGS Show Frame "${label}" has received a revealFail event. This does not necessarily mean the frame will not load. There is a timer going that is waiting for the revealSuccess event. If that does not come through we will consider it failed.`,
      );
    });
    frame.on('requestFail', frameFail);
  }, [frame, label, onRevealFail, revealSuccess, useFieldStyling]);

  useEffect(() => {
    if (value === undefined || value === null) {
      revealSuccess();
    }
  }, [revealSuccess, value]);

  if (type === 'file') {
    return <SnapIcon icon={'archive'} color={'#94A3B8'} />;
  }

  return (
    <BaseInput
      label={label}
      border={useFieldStyling ? '1px solid #CBD5E1' : undefined}
      backgroundColor={useFieldStyling ? '#efefef' : undefined}
      labelClassName='vgs-show-label'
    >
      <div className='vgs-ref font-medium vgs-show' ref={elRef}></div>
    </BaseInput>
  );
}
